/**
 * Aggregated page state for User Management (modals, selection, form).
 *
 * Extracted from useUserManagement (Sub-PR 6.3).
 */

import { useState } from 'react'
import type { AvailableLicenseForSeat } from '../../services/admin'
import type { User } from '../../types'
import type { CreateUserForm } from './types'

interface ModalFlags {
  showCreateModal: boolean
  setShowCreateModal: (v: boolean) => void
  showEditModal: boolean
  setShowEditModal: (v: boolean) => void
  showDeleteModal: boolean
  setShowDeleteModal: (v: boolean) => void
  showSeatModal: boolean
  setShowSeatModal: (v: boolean) => void
  showReactivateModal: boolean
  setShowReactivateModal: (v: boolean) => void
  showResetPasswordModal: boolean
  /** One-time password just issued by a create or reset, shown once then discarded. */
  issuedPassword: string | null
  setIssuedPassword: (v: string | null) => void
  setShowResetPasswordModal: (v: boolean) => void
}

interface SelectionState {
  selectedUser: User | null
  setSelectedUser: (user: User | null) => void
  selectedLicense: string
  setSelectedLicense: (id: string) => void
  reactivateLicenses: AvailableLicenseForSeat[]
  setReactivateLicenses: (lics: AvailableLicenseForSeat[]) => void
  userToReactivate: User | null
  setUserToReactivate: (user: User | null) => void
  actionMenuUser: string | null
  setActionMenuUser: (id: string | null) => void
}

interface FormState {
  formData: CreateUserForm
  setFormData: (data: CreateUserForm) => void
  formLoading: boolean
  setFormLoading: (v: boolean) => void
  formError: string | null
  setFormError: (msg: string | null) => void
}

export type UserPageState = ModalFlags & SelectionState & FormState

export function useUserPageState(initialForm: CreateUserForm): UserPageState {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSeatModal, setShowSeatModal] = useState(false)
  const [showReactivateModal, setShowReactivateModal] = useState(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [issuedPassword, setIssuedPassword] = useState<string | null>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedLicense, setSelectedLicense] = useState<string>('')
  const [reactivateLicenses, setReactivateLicenses] = useState<AvailableLicenseForSeat[]>([])
  const [userToReactivate, setUserToReactivate] = useState<User | null>(null)
  const [actionMenuUser, setActionMenuUser] = useState<string | null>(null)
  const [formData, setFormData] = useState<CreateUserForm>(initialForm)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  return {
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    showDeleteModal,
    setShowDeleteModal,
    showSeatModal,
    setShowSeatModal,
    showReactivateModal,
    setShowReactivateModal,
    showResetPasswordModal,
    issuedPassword,
    setIssuedPassword,
    setShowResetPasswordModal,
    selectedUser,
    setSelectedUser,
    selectedLicense,
    setSelectedLicense,
    reactivateLicenses,
    setReactivateLicenses,
    userToReactivate,
    setUserToReactivate,
    actionMenuUser,
    setActionMenuUser,
    formData,
    setFormData,
    formLoading,
    setFormLoading,
    formError,
    setFormError,
  }
}
